import { Link, useLocation, useParams } from 'react-router-dom'
import { useBooking } from '../api/queries'
import type { SkippedDate } from '../api/types'
import { Badge, Button, Loading, PageTitle } from '../components/ui'
import { dateKey, fmtDate, fmtDuration, fmtRange, minutesBetween, parse } from '../lib/time'
import { plural } from '../lib/words'

/**
 * Экран 3.4 «Мобильный: готово».
 *
 * Отдельный экран, а не всплывашка «сохранено», потому что вопрос у человека ровно один
 * и он не про бронь: «а участники-то узнают?». Бронь он и так видит в расписании,
 * а вот что произошло с приглашёнными — больше нигде не написано. Поэтому здесь
 * по пунктам: кому ушло письмо, у кого ещё нет учётной записи, кого позвали по адресу.
 *
 * Форма передаёт список приглашённых через состояние перехода: у участника из ответа
 * сервера нет признака «заведён только что», а разница между «коллега получил письмо»
 * и «человека в системе не было, приглашение ушло на адрес» — как раз то, что человек
 * пришёл сюда узнать.
 */

/**
 * Что известно только в момент создания и чего в брони уже не прочитать.
 *
 * GET /bookings/{id} отдаёт бронь как она есть, а warning и skipped — про само
 * создание: их говорят один раз, в ответе на POST. Не передать их сюда значит
 * потерять насовсем: человек нажал «Пропустить занятые даты» и так и не узнал,
 * какие именно даты пропущены.
 */
export interface BookingDoneHint {
  invitedByEmail: string[]
  seriesUntil?: string
  /** Предупреждение, а не отказ: например, приглашённых больше, чем мест. */
  warning?: string
  /** Даты серии, которые оказались заняты и потому пропущены. */
  skipped?: SkippedDate[]
}

export function BookingDonePage() {
  const { bookingId: bookingIdParam } = useParams()
  const bookingId = Number(bookingIdParam)
  const { state } = useLocation()
  const hint = (state as { done?: BookingDoneHint } | null)?.done

  const { data: booking, isLoading } = useBooking(Number.isFinite(bookingId) ? bookingId : undefined)

  if (isLoading) return <Loading text="Проверяем, что всё сохранилось…" />
  if (!booking) {
    return (
      <div className="card p-6">
        <p className="font-medium">Встречу не нашли</p>
        <p className="mt-2 text-sm text-muted">
          Возможно, её уже отменили. Посмотрите список встреч — там видно всё, что есть.
        </p>
        <Link to="/meetings" className="mt-4 inline-block">
          <Button variant="primary">К встречам</Button>
        </Link>
      </div>
    )
  }

  const participants = booking.participants ?? []
  const invitedByEmail = new Set((hint?.invitedByEmail ?? []).map((email) => email.toLowerCase()))
  const known = participants.filter(
    (participant) => !invitedByEmail.has(participant.email.toLowerCase()),
  )
  const fresh = participants.filter((participant) =>
    invitedByEmail.has(participant.email.toLowerCase()),
  )

  return (
    <div className="mx-auto max-w-xl">
      <PageTitle>Готово</PageTitle>

      <div className="card flex flex-col gap-3 p-4">
        <p className="text-lg font-medium">{booking.title || 'Без темы'}</p>
        <p className="tnum text-sm text-muted">
          {booking.room?.name ?? `Комната #${booking.room_id}`}
          <br />
          {fmtDate(booking.starts_at)}, {fmtRange(booking.starts_at, booking.ends_at)}
          <br />
          {fmtDuration(minutesBetween(booking.starts_at, booking.ends_at))}
        </p>
        {booking.recurrence && (
          <div>
            <Badge tone="accent">{booking.recurrence.human_readable ?? 'Повторяющаяся серия'}</Badge>
          </div>
        )}
      </div>

      {/* Сервер предупредил, но не отказал: например, приглашённых больше, чем мест.
          Промолчать здесь — значит дать человеку уйти с уверенностью, что всё в порядке. */}
      {hint?.warning && (
        <div
          role="status"
          className="card mt-4 border-state-soon/50 bg-state-soon/10 px-4 py-3 text-sm"
        >
          <p className="font-medium">Обратите внимание</p>
          <p className="mt-1 text-muted">{hint.warning}</p>
        </div>
      )}

      {/* «Пропустить занятые даты» человек нажал сам, но какие именно даты пропущены,
          знает только ответ на создание. Не показать их — оставить его с серией,
          в которой чего-то не хватает, и без единого способа узнать чего. */}
      {hint?.skipped && hint.skipped.length > 0 && (
        <section className="mt-4">
          <h2 className="mb-2 text-base font-semibold">
            {hint.skipped.length}{' '}
            {plural(hint.skipped.length, 'дата пропущена', 'даты пропущены', 'дат пропущено')}
          </h2>
          <ul className="flex flex-col gap-2">
            {hint.skipped.map((skip) => (
              <li key={skip.date} className="card px-4 py-3 text-sm">
                <span className="tnum font-medium">{fmtDate(skip.date)}</span>
                <span className="mt-0.5 block text-muted">{skip.reason}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2 hint">
            Остальные встречи серии созданы. На пропущенные даты соберитесь в другой
            комнате или в другое время — их можно забронировать отдельно.
          </p>
        </section>
      )}

      <section className="mt-5">
        <h2 className="mb-2 text-base font-semibold">Что произошло с участниками</h2>

        {participants.length === 0 ? (
          <p className="hint">
            Вы забронировали комнату для себя — приглашения никому не уходили. Позвать коллег
            можно позже: откройте встречу и добавьте их, письма уйдут только новым.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {known.map((participant) => (
              <li key={participant.email} className="card flex flex-col px-4 py-3">
                <span className="font-medium">{participant.display_name}</span>
                <span className="text-sm text-muted">
                  Приглашение ушло на {participant.email} — с файлом календаря.
                </span>
              </li>
            ))}
            {fresh.map((participant) => (
              <li
                key={participant.email}
                className="rounded-card border border-dashed border-accent/50 bg-accent-soft/40 px-4 py-3"
              >
                <span className="tnum font-medium">{participant.email}</span>
                <span className="mt-0.5 block text-sm text-fg/80">
                  Этого человека в системе не было — приглашение ушло на адрес, а учётная
                  запись заведётся при первом входе.
                </span>
              </li>
            ))}
          </ul>
        )}

        {booking.recurrence && (
          <p className="mt-3 hint">
            Серия хранится правилом: встречи дальше горизонта бронирования появятся сами,
            по мере приближения, и приглашения к ним уйдут тогда же.
          </p>
        )}
      </section>

      <div className="mt-6 flex flex-wrap gap-2">
        {/* День брони по местному времени офиса, а не по UTC: вечерняя встреча иначе
            уводит на следующий день. */}
        <Link to={`/?date=${dateKey(parse(booking.starts_at))}`}>
          <Button variant="primary">К расписанию</Button>
        </Link>
        <Link to="/meetings">
          <Button variant="ghost">Мои встречи</Button>
        </Link>
        <Link to={`/bookings/${booking.id}/edit`}>
          <Button variant="ghost">Изменить встречу</Button>
        </Link>
      </div>
    </div>
  )
}
